import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Key, Lock, Smartphone, CheckCircle, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const Security = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Admin access
  const [clickCount, setClickCount] = useState(0);
  const [showPassphrasePrompt, setShowPassphrasePrompt] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [clickTimer, setClickTimer] = useState(null);

  // Triple-click/tap handler (works on all devices)
  const handleAdminAccess = (e) => {
    // Prevent default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    console.log(`Admin passphrase access click: ${newCount}/3`);

    // Clear previous timer
    if (clickTimer) {
      clearTimeout(clickTimer);
    }

    // Set new timer to reset count after 1.5 seconds (longer for mobile)
    const timer = setTimeout(() => {
      setClickCount(0);
      console.log('Admin passphrase click count reset');
    }, 1500);
    setClickTimer(timer);

    // If triple-clicked, show passphrase prompt
    if (newCount === 3) {
      console.log('Triple-click detected - showing passphrase prompt');
      setShowPassphrasePrompt(true);
      setClickCount(0);
      clearTimeout(timer);
    }
  };

  const verifyPassphrase = async () => {
    // PERMANENT PASSPHRASE - NEVER CHANGES
    const PERMANENT_ADMIN_PASSPHRASE = 'EDGE2024ADMIN';
    
    console.log('🔐 Passphrase verification started');
    console.log('Entered passphrase:', passphrase);
    console.log('Expected passphrase:', PERMANENT_ADMIN_PASSPHRASE);
    console.log('Match:', passphrase === PERMANENT_ADMIN_PASSPHRASE);
    
    if (passphrase === PERMANENT_ADMIN_PASSPHRASE) {
      console.log('✅ Passphrase correct! Starting admin login...');
      setShowPassphrasePrompt(false);
      setPassphrase('');
      
      // Auto-login as admin - no further security checks required
      try {
        const BACKEND_URL = '';
        console.log('Backend URL:', BACKEND_URL);
        
        const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: 'admin',
            password: 'admin123'
          })
        });

        console.log('Admin login response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('Admin login successful!', data);
          
          // Store admin credentials
          localStorage.setItem('admin_token', data.token);
          localStorage.setItem('secure_admin_token', data.token); // Also store as secure_admin_token for SecureAdminLayout
          localStorage.setItem('admin_user', JSON.stringify(data.admin));
          localStorage.setItem('admin_session_start', Date.now().toString()); // Session start time
          localStorage.setItem('passphrase_access', 'true');
          sessionStorage.setItem('security_clearance', 'granted');
          sessionStorage.setItem('clearance_timestamp', Date.now().toString());
          
          console.log('✅ Admin credentials stored');
          
          toast({
            title: '✅ Admin Access Granted',
            description: 'Welcome to Admin Panel',
          });
          
          console.log('Navigating to admin dashboard in 500ms...');
          
          // Direct access to admin dashboard - bypass all security
          setTimeout(() => {
            console.log('Executing navigation to /secure-admin-access/dashboard');
            navigate('/secure-admin-access/dashboard');
          }, 500);
        } else {
          const errorData = await response.text();
          console.error('Admin login failed:', response.status, errorData);
          toast({
            title: 'System Error',
            description: 'Admin authentication failed',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Admin auto-login error:', error);
        toast({
          title: 'Connection Error',
          description: 'Unable to connect to admin system',
          variant: 'destructive'
        });
      }
    } else {
      console.log('❌ Invalid passphrase');
      toast({
        title: 'Access Denied',
        description: 'Invalid security passphrase',
        variant: 'destructive'
      });
      setPassphrase('');
    }
  };

  const handlePasswordChange = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: 'Error', description: 'Please fill all fields', variant: 'destructive' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    toast({ title: 'Success', description: 'Password updated successfully' });
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const securityFeatures = [
    {
      title: 'Two-Factor Authentication',
      description: 'Add an extra layer of security',
      icon: Shield,
      status: 'inactive',
      action: 'Enable',
      color: 'from-blue-400 to-blue-500',
      onClick: () => toast({ title: 'Coming Soon', description: 'Enable feature coming soon' })
    },
    {
      title: 'Biometric Login',
      description: 'Use fingerprint or face ID',
      icon: Smartphone,
      status: 'inactive',
      action: 'Setup',
      color: 'from-purple-400 to-purple-500',
      onClick: () => toast({ title: 'Coming Soon', description: 'Setup feature coming soon' })
    },
    {
      title: 'Login History',
      description: 'View recent login activity',
      icon: Key,
      status: 'active',
      action: 'View',
      color: 'from-green-400 to-green-500',
      onClick: () => toast({ title: 'Coming Soon', description: 'View feature coming soon' })
    },
    {
      title: 'Advanced Security Center',
      description: 'Access enhanced security controls',
      icon: ShieldCheck,
      status: 'restricted',
      action: 'Access',
      color: 'from-red-500 to-orange-500',
      onClick: handleAdminAccess
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-lg font-bold text-cyan-400">Security</h1>
          <div className="w-16"></div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {/* Security Status */}
        <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-bold text-cyan-400">Security Score</h3>
          </div>
          <div className="text-3xl font-bold text-cyan-400 mb-2">75/100</div>
          <p className="text-sm text-cyan-600">Your account has good security. Enable 2FA to improve.</p>
        </div>

        {/* Change Password */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </h2>
          <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4 space-y-4">
            <div>
              <Label className="text-cyan-400">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-2 bg-slate-900/50 border-cyan-500/30 text-cyan-100"
              />
            </div>
            <div>
              <Label className="text-cyan-400">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-2 bg-slate-900/50 border-cyan-500/30 text-cyan-100"
              />
            </div>
            <div>
              <Label className="text-cyan-400">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-2 bg-slate-900/50 border-cyan-500/30 text-cyan-100"
              />
            </div>
            <Button
              onClick={handlePasswordChange}
              className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white neon-button"
            >
              Update Password
            </Button>
          </div>
        </div>

        {/* Security Features */}
        <div>
          <h2 className="text-lg font-bold text-cyan-400 mb-4">Security Features</h2>
          {securityFeatures.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <div
                key={idx}
                className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4 mb-3 hover:bg-slate-800/70 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-cyan-300">{feature.title}</div>
                      <div className="text-xs text-cyan-700">{feature.description}</div>
                    </div>
                  </div>
                  <Button
                    onClick={feature.onClick}
                    onTouchEnd={feature.onClick}
                    type="button"
                    className={`h-9 px-4 active:scale-95 transition-transform ${
                      feature.status === 'active'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : feature.status === 'restricted'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                        : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    }`}
                  >
                    {feature.action}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Security Tips */}
        <div className="mt-6 bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
          <div className="flex gap-2">
            <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-cyan-600">
              <p className="font-semibold text-cyan-400 mb-2">Security Tips</p>
              <ul className="space-y-1 text-xs">
                <li>• Never share your password with anyone</li>
                <li>• Enable two-factor authentication</li>
                <li>• Use a strong, unique password</li>
                <li>• Review login history regularly</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Passphrase Prompt Modal */}
      {showPassphrasePrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-cyan-500/30 rounded-xl p-6 mx-4 w-full max-w-md">
            <div className="text-center mb-4">
              <ShieldCheck className="w-12 h-12 text-cyan-400 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-cyan-400">Security Access</h3>
              <p className="text-sm text-cyan-600">Enter security passphrase to continue</p>
            </div>
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Enter passphrase"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className="bg-slate-900/50 border-cyan-500/30 text-cyan-100"
                onKeyPress={(e) => e.key === 'Enter' && verifyPassphrase()}
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowPassphrasePrompt(false);
                    setPassphrase('');
                  }}
                  className="flex-1 bg-slate-600 hover:bg-slate-500 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={verifyPassphrase}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white"
                >
                  Access
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Security;